// Adónde mandar al usuario después de un login exitoso.
//
// Existe porque `/planes` es pública pero contratar necesita sesión: quien
// elige un plan sin estar logueado tiene que poder volver a donde estaba en vez
// de aterrizar en el inicio y perder lo que iba a hacer.
//
// Va por storage y no como parámetro de función a propósito: el login tiene SEIS
// caminos de éxito repartidos en dos archivos (usuario y contraseña, sin
// establecimiento, offline, "continuar como", Google nativo y Google web por
// redirect). Enhebrar un `next` por todas esas firmas es más superficie para
// equivocarse, y el de Google web ni siquiera sobrevive el ida y vuelta a Google.
//
// Es localStorage y NO sessionStorage, que es lo que había antes. sessionStorage
// vive por pestaña: en el celular, el mail se abre en el navegador chico de
// Gmail y al entrar con Google el teléfono salta al navegador grande. Pestaña
// nueva, destino perdido, y la persona aterriza en el inicio con la compra a
// medio hacer. Eso es exactamente lo que se reportó.
//
// Lo que localStorage no tiene y sessionStorage sí es el borrado automático al
// cerrar la pestaña, y de ahí sale el vencimiento: un destino sirve para el
// login que está pasando ahora, no para el de mañana.

const CLAVE = "destinoPostLogin";

/**
 * Cuánto vale un destino guardado. Quince minutos es de sobra para ir al login y
 * volver —incluso con el rodeo por Google— y poco para que reaparezca en el
 * próximo login de otra persona en la misma máquina.
 */
const VIGENCIA_MS = 15 * 60 * 1000;

/**
 * Valida un destino antes de confiar en él.
 *
 * Solo se aceptan rutas internas: cualquier cosa que pueda apuntar a otro sitio
 * se descarta. Sin esto, un `?next=https://sitio-falso/` convierte al login en
 * un redirector abierto — el clásico para armar un phishing que arranca en el
 * dominio real.
 */
export function rutaSegura(destino) {
    if (typeof destino !== "string") return null;
    const limpio = destino.trim();
    if (!limpio.startsWith("/")) return null;
    // `//host` es una URL protocolo-relativa: se ve como ruta y no lo es.
    if (limpio.startsWith("//")) return null;
    if (limpio.includes("://") || limpio.includes("\\")) return null;
    return limpio;
}

/** Recuerda a dónde volver. Ignora en silencio los destinos que no son internos. */
export function guardarDestino(destino) {
    const seguro = rutaSegura(destino);
    if (!seguro) return;
    try {
        localStorage.setItem(CLAVE, JSON.stringify({ ruta: seguro, ts: Date.now() }));
    } catch {
        // Modo privado o storage lleno: se pierde el destino, no el login.
    }
}

/**
 * El destino guardado, o el inicio si no hay ninguno o si ya venció. Lo consume:
 * un destino sirve una sola vez, para que no reaparezca en el próximo login de
 * otra persona en el mismo navegador.
 */
export function tomarDestino(pre = "") {
    let crudo = null;
    try {
        crudo = localStorage.getItem(CLAVE);
        localStorage.removeItem(CLAVE);
    } catch {
        crudo = null;
    }

    let guardado = null;
    try {
        const dato = crudo ? JSON.parse(crudo) : null;
        // Un destino vencido es igual que no tener ninguno.
        if (dato && Date.now() - dato.ts < VIGENCIA_MS) {
            guardado = dato.ruta;
        }
    } catch {
        // Lo que haya quedado del formato viejo (una ruta pelada) se descarta:
        // no se sabe de cuándo es, y adivinar es peor que mandar al inicio.
        guardado = null;
    }

    const seguro = rutaSegura(guardado);
    return seguro ? pre + seguro : pre + "/";
}

/**
 * Las rutas a las que se puede mandar a alguien que entró y todavía NO tiene
 * establecimiento.
 *
 * El resto va al onboarding: con un token base (sin establecimiento en scope)
 * cualquier llamada de negocio devuelve 401, el front lo lee como sesión
 * vencida y rebota al login en bucle. Estas dos no llaman a nada de eso —
 * contratar un plan no necesita campo— así que son seguras.
 */
const RUTAS_SIN_ESTABLECIMIENTO = ["/planes", "/pagos"];

/**
 * El destino guardado cuando la persona no tiene establecimiento, o {@code null}
 * si no hay ninguno o si el que hay necesita uno.
 *
 * Existe porque quien viene del mail a comprar y todavía no creó su campo estaba
 * cayendo en la pantalla de crear establecimiento con el destino tirado a la
 * basura: elegía el plan, se logueaba, y no había forma de volver a la compra.
 *
 * Consume el destino en los dos casos: si no se puede usar, tampoco tiene que
 * reaparecer en el próximo login.
 */
export function tomarDestinoSinEstablecimiento(pre = "") {
    const destino = tomarDestino(pre);
    const ruta = pre && destino.startsWith(pre) ? destino.slice(pre.length) : destino;
    return RUTAS_SIN_ESTABLECIMIENTO.some((r) => ruta === r || ruta.startsWith(r + "/") || ruta.startsWith(r + "?"))
        ? destino
        : null;
}
